import { globsOverlap, literalPrefix, matchGlob, samples } from "./glob.ts";

const FILE_EXTENSIONS = new Set(
  "ts tsx mts cts js jsx mjs cjs json jsonc md mdx yml yaml toml ini cfg conf css scss sass less html htm svg png jpg jpeg gif ico webp sql sh bash zsh py rb go rs java kt kts gradle properties prisma graphql gql proto svelte vue astro php c h cc cpp hpp cs swift txt lock env xml csv tsv log patch snap map wasm mod sum lockb".split(" "),
);

/**
 * True when an area token can be fenced: it has a "/" or a "*", carries a scheme (`url:`, `roomate:`),
 * is a dotfile, names a file with a known extension (`package.json`, `schema.prisma`), or is a
 * well-known extensionless root file. Symbol names such as `planReview`, `NodeDTO.runtimes`,
 * `migration` or `public_api` are not paths, so routing ignores them.
 */
export function isPathArea(area: string): boolean {
  const token = area.trim();
  // Scheme-prefixed scopes (url:, roomate:conversations) are fenced like paths: dropping them would let any agent fit.
  if (/^[a-z][a-z0-9-]*:/.test(token)) return true;
  if (token.includes("/") || token.includes("*")) return true;
  // Dotfiles (`.gitignore`, `.env.local`) and suffixed container files (`Dockerfile.dev`).
  if (/^\.[A-Za-z][\w-]*$/.test(token) || /^\.env\.[\w.-]+$/.test(token)) return true;
  if (/^(Dockerfile|Containerfile)\.[\w.-]+$/.test(token)) return true;
  if (ROOT_FILES.test(token)) return true;
  const ext = /^[^\s]+\.([A-Za-z0-9]+)$/.exec(token)?.[1];
  return ext !== undefined && FILE_EXTENSIONS.has(ext.toLowerCase());
}

/** Well-known extensionless root files (`Pipfile`, `justfile`, `CNAME`, `VERSION`, ...). */
const ROOT_FILES = /^(Dockerfile|Containerfile|Makefile|GNUmakefile|Justfile|justfile|Vagrantfile|Pipfile|Gemfile|Rakefile|Procfile|Brewfile|Jenkinsfile|Caddyfile|Tiltfile|LICENSE|LICENCE|NOTICE|AUTHORS|CODEOWNERS|CNAME|VERSION|README|CHANGELOG|COPYING|BUILD|WORKSPACE|OWNERS|Taskfile|gradlew|mvnw)$/;

/**
 * An agent fence token: any path area (globs, files, `url:` and `roomate:` scopes). A label like `web`
 * can never match a task area (#1446), so it is refused on write.
 */
export function isFenceToken(token: string): boolean {
  return isPathArea(token);
}

/** The tokens of `fences` that are not fence tokens, in order and without repeats. */
export function invalidFences(fences: readonly string[]): string[] {
  return [...new Set(fences.filter((f) => !isFenceToken(f)))];
}

/** The path-glob tokens of `areas`; what routing and fence checks look at. */
export function routableAreas(areas: readonly string[]): string[] {
  return areas.filter(isPathArea);
}

/** The tokens of `areas` that are not path globs, in order and without repeats. */
export function nonPathAreas(areas: readonly string[]): string[] {
  return [...new Set(areas.filter((a) => !isPathArea(a)))];
}

/**
 * True when every area is covered by some allowed glob. Both an empty `allowedAreas`
 * (unfenced agent) and an empty `areas` (no areas to check) vacuously fit.
 */
export function areasFit(allowedAreas: readonly string[], rawAreas: readonly string[]): boolean {
  // A legacy label fence (`web`, stored before fences were validated) still matches the same token by equality (#1446).
  const fences = allowedAreas.map((f) => f.trim());
  const areas = rawAreas.filter((a) => isPathArea(a) || fences.includes(a.trim()));
  if (allowedAreas.length === 0) return true;
  // Only symbol tokens: nothing can be checked against a fence, so a fenced agent must not fit by default.
  if (areas.length === 0) return rawAreas.length === 0;
  return areas.every((area) => allowedAreas.some((allowed) => globsOverlap(allowed, area)));
}

export type AreaFitAgent = { id: string; name: string; allowedAreas: readonly string[] };

export type AreaFitReport = {
  /** Agents whose allowed areas cover every one of the task's areas. */
  fits: { actorId: string; name: string }[];
  /** For each task area, the agents that cover it. */
  perArea: { area: string; actorIds: string[] }[];
  /** Areas no agent in the roster covers. */
  uncovered: string[];
  /** Agents covering some but not all of the task's areas, with the subset each covers. */
  partial: { actorId: string; name: string; covers: string[] }[];
};

type Specificity = { covers: boolean; prefix: number };

function compareSpecificity(x: Specificity, y: Specificity): number {
  return Number(x.covers) - Number(y.covers) || x.prefix - y.prefix;
}

/** Whether `fence` matches every sample path of `area`, the same sampling globsOverlap uses. */
function fenceCovers(fence: string, area: string): boolean {
  return samples(area).every((path) => matchGlob(fence, path));
}

/**
 * How specifically `agent`'s fences target `area`. A fence that covers the whole area always
 * outranks one that only overlaps it: globsOverlap is loose, so a narrower fence can overlap
 * the area and still not satisfy it, however long its literal prefix is.
 */
function specificityFor(agent: AreaFitAgent, area: string): Specificity {
  if (agent.allowedAreas.length === 0) return { covers: true, prefix: 0 };
  let best: Specificity = { covers: false, prefix: 0 };
  for (const allowed of agent.allowedAreas) {
    if (!globsOverlap(allowed, area)) continue;
    const candidate = { covers: fenceCovers(allowed, area), prefix: literalPrefix(allowed).length };
    if (compareSpecificity(candidate, best) > 0) best = candidate;
  }
  return best;
}

/**
 * Coverage of a task's areas by a roster of agents. The caller is responsible for
 * filtering the roster to active, non-viewer agents with build capacity.
 *
 * An empty `areas` list fits every agent vacuously (matching `areasFit`'s own empty-areas
 * case): every agent lands in `fits`, `perArea` and `uncovered` are both `[]`.
 */
export function areaFitReport(agents: readonly AreaFitAgent[], rawAreas: readonly string[]): AreaFitReport {
  const areas = [...new Set(routableAreas(rawAreas))];
  const perArea = areas.map((area) => {
    const covering = agents.filter((a) => areasFit(a.allowedAreas, [area]));
    // Most specific fence first: this ordering is the suggested owner, so a catch-all
    // fence (merge-agent's "**", or an unfenced agent) must not displace the agent
    // whose fence actually targets this area.
    const actorIds = covering
      .map((a) => ({ a, specificity: specificityFor(a, area) }))
      .sort((x, y) => compareSpecificity(y.specificity, x.specificity) || x.a.name.localeCompare(y.a.name))
      .map((x) => x.a.id);
    return { area, actorIds };
  });
  const uncovered = perArea.filter((p) => p.actorIds.length === 0).map((p) => p.area);

  const fits: { actorId: string; name: string }[] = [];
  const partial: { actorId: string; name: string; covers: string[] }[] = [];
  for (const agent of agents) {
    const covers = areas.filter((area) => areasFit(agent.allowedAreas, [area]));
    if (covers.length === areas.length && areasFit(agent.allowedAreas, rawAreas)) {
      fits.push({ actorId: agent.id, name: agent.name });
    } else if (covers.length > 0) {
      partial.push({ actorId: agent.id, name: agent.name, covers });
    }
  }

  return { fits, perArea, uncovered, partial };
}
